/**
 * Компонент QR-авторизации для MAX Client
 */
import { useEffect, useState } from 'react';
import { Button, Card, CardHeader, CardTitle, CardContent, Spinner } from '@/components/ui';
import { startQRAuth, checkAuthStatus, type QRAuthResult, type AuthStatus } from '@/api/maxClient';
import { QRCodeSVG } from 'qrcode.react';
import { X } from 'lucide-react';

interface MaxQRAuthProps {
  onSuccess: (status: AuthStatus) => void;
  onCancel: () => void;
  tenantId?: string;
}

export function MaxQRAuth({ onSuccess, onCancel, tenantId }: MaxQRAuthProps) {
  const [qrData, setQrData] = useState<QRAuthResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function initQRAuth() {
      if (!tenantId) {
        setError('Выберите клинику для QR-авторизации MAX');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const result = await startQRAuth(tenantId);
        if (mounted) {
          setQrData(result);
          setLoading(false);
          startStatusCheck();
        }
      } catch (err: any) {
        if (mounted) {
          setError(err.response?.data?.error || 'Не удалось запустить QR-авторизацию MAX');
          setLoading(false);
        }
      }
    }

    initQRAuth();

    return () => {
      mounted = false;
    };
  }, [tenantId]);

  const startStatusCheck = () => {
    const interval = setInterval(async () => {
      try {
        setChecking(true);
        const status = await checkAuthStatus(tenantId);
        if (status.status === 'connected') {
          clearInterval(interval);
          onSuccess(status);
        }
      } catch (err) {
        console.error('Failed to check MAX auth status:', err);
      } finally {
        setChecking(false);
      }
    }, 3000);

    setTimeout(() => clearInterval(interval), 5 * 60 * 1000);
  };

  return (
    <Card className="border-violet-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>QR-авторизация MAX</CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && (
          <div className="flex flex-col items-center py-8">
            <Spinner size="lg" />
            <p className="text-sm text-neutral-500 mt-4">Генерация QR-кода...</p>
          </div>
        )}

        {error && (
          <div className="text-center py-4">
            <p className="text-sm text-red-600">{error}</p>
            <Button onClick={onCancel} variant="secondary" className="mt-4">
              Закрыть
            </Button>
          </div>
        )}

        {qrData && !error && (
          <div className="flex flex-col items-center space-y-4">
            <div className="bg-white p-4 rounded-lg border border-neutral-200">
              <QRCodeSVG value={qrData.qr_code_data || qrData.qr_code_url} size={256} />
            </div>
            <div className="text-center space-y-2">
              <p className="text-sm font-medium text-neutral-700">
                Отсканируйте QR-код в приложении MAX
              </p>
              <p className="text-xs text-neutral-500">
                Профиль → Устройства → Войти по QR-коду
              </p>
              {checking && (
                <p className="text-xs text-violet-600 flex items-center justify-center gap-2">
                  <Spinner size="sm" />
                  Ожидание сканирования...
                </p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
